import { useCallback, useMemo } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { GetWorkflowDocument, StagesByWorkflowDocument } from "#app/lib/data";

export function useStagesList(workflowId: string) {
  const apolloClient = useApolloClient();

  const workflowVars = useMemo(() => ({ id: workflowId }), [workflowId]);
  const stagesVars = useMemo(() => ({ workflowId }), [workflowId]);

  const { data: workflowData } = useQuery(GetWorkflowDocument, {
    variables: workflowVars,
    fetchPolicy: "cache-first",
  });

  const {
    data: stagesData,
    loading,
    error,
    refetch,
  } = useQuery(StagesByWorkflowDocument, {
    variables: stagesVars,
    fetchPolicy: "cache-and-network",
  });

  const workflowName = workflowData?.workflow?.name ?? "Workflow";

  const stages = useMemo(
    () => stagesData?.stages?.edges?.map((e) => e.node) ?? [],
    [stagesData],
  );

  const optimisticUpdateSortOrder = useCallback(
    (stageId: string, newSortOrder: number) => {
      // `identify` answers `undefined` for an object it cannot key, and the
      // cache reads an absent `id` as the ROOT — the edit would land on
      // ROOT_QUERY.
      const cacheId = apolloClient.cache.identify({
        __typename: "Stage",
        stageId,
      });
      if (!cacheId) return;

      apolloClient.cache.modify({
        id: cacheId,
        fields: { sortOrder: () => newSortOrder },
      });
    },
    [apolloClient],
  );

  return {
    workflowName,
    stages,
    loading,
    error,
    refetch,
    optimisticUpdateSortOrder,
  };
}
